import * as React from "react";
import {
  CAPTION_TT_DIALOG_LOAD_ERROR,
  CAPTION_TT_LABEL_SUBTOTAL,
  CAPTION_TT_LABEL_SUM,
  CAPTION_TT_LINK_SHOW_DETAILS,
  CAPTION_TT_RESULT_CORRECT,
  CAPTION_TT_RESULT_INCORRECT,
  CAPTION_TT_RESULT_STRUCTURE_CHANGED,
  CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED,
  CAPTION_TT_TITLE_DIFFERENCES,
  TABLE_TOTALS_CAPTION_DEFAULTS,
} from "../document/validation/validationCaptions";
import { formatMessage, formatMessageWithParamsDefault } from "../../utils/helpers/LanguageHelper";
import { TableTotalsDialogPayload, TableTotalsResultModel } from "./tableTotals/tableTotalsResult.types";
import { openTableTotalsDetailsDialog } from "../../office/TableTotalsDetailsDialog";
import { getPalette } from "./RuleResultItem";

export interface TableTotalsResultContentProps {
  model: TableTotalsResultModel;
}

function resolveStatusCaption(model: TableTotalsResultModel): string {
  switch (model.status) {
    case "VERIFIED":
      return formatMessageWithParamsDefault(
        CAPTION_TT_RESULT_CORRECT,
        TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_RESULT_CORRECT],
        {}
      );
    case "FAILED":
      return formatMessageWithParamsDefault(
        CAPTION_TT_RESULT_INCORRECT,
        TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_RESULT_INCORRECT],
        {}
      );
    case "ERROR":
    default:
      if (model.errorReason === "NON_NUMERIC_VALUE") {
        return formatMessageWithParamsDefault(
          CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED,
          TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED],
          {}
        );
      }
      return formatMessageWithParamsDefault(
        CAPTION_TT_RESULT_STRUCTURE_CHANGED,
        TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_RESULT_STRUCTURE_CHANGED],
        {}
      );
  }
}

export const TableTotalsResultContent: React.FC<TableTotalsResultContentProps> = ({ model }) => {
  const palette = getPalette(model.status);
  const statusCaption = resolveStatusCaption(model);
  const showDetails = model.status !== "ERROR";

  const handleShowDetails = React.useCallback(() => {
    const payload: TableTotalsDialogPayload = {
      snapshot: model.snapshot,
      addends: model.addends,
      subtotal: model.subtotal,
      status: model.status,
      title: formatMessageWithParamsDefault(
        CAPTION_TT_TITLE_DIFFERENCES,
        TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_TITLE_DIFFERENCES],
        { tableName: model.tableName ?? "" }
      ),
      labels: {
        sum: formatMessageWithParamsDefault(
          CAPTION_TT_LABEL_SUM,
          TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_LABEL_SUM],
          {}
        ),
        subtotal: formatMessageWithParamsDefault(
          CAPTION_TT_LABEL_SUBTOTAL,
          TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_LABEL_SUBTOTAL],
          {}
        ),
        close: formatMessage("cof.base.close"),
        errorMessage: formatMessageWithParamsDefault(
          CAPTION_TT_DIALOG_LOAD_ERROR,
          TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_DIALOG_LOAD_ERROR],
          {}
        ),
      },
    };
    openTableTotalsDetailsDialog(payload);
  }, [model]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 8 }}>
      <div style={{ color: palette.accent, fontSize: 12 }}>{statusCaption}</div>

      {showDetails && model.displayExpression && <div style={{ color: "#111827" }}>{model.displayExpression}</div>}

      {showDetails && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={handleShowDetails}
            style={{
              color: palette.link,
              fontSize: 12,
              textDecoration: "underline",
              whiteSpace: "nowrap",
              background: "none",
              border: "none",
              padding: 0,
              cursor: "pointer",
            }}
          >
            {formatMessageWithParamsDefault(
              CAPTION_TT_LINK_SHOW_DETAILS,
              TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_LINK_SHOW_DETAILS],
              {}
            )}
          </button>
        </div>
      )}
    </div>
  );
};
